import { toJsDate } from "../utils/toJsDate";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  useActivityActions,
  useActivityAuditData,
  useActivityFeedData,
} from "../contexts/ActivityContext";
import { usePageTracking } from "../hooks/usePageTracking";
import {
  createMatchNavigationTarget,
  resolveProfileJoinError,
  resolveProfileJoinResult,
  selectProfileJoinPrefetchedActivity,
} from "../utils/pageNavigationFlows";
import { useToast } from "../contexts/ToastContext";
import {
  ArrowLeftIcon,
  LockIcon,
  MoreVerticalIcon,
  Edit2Icon,
  Trash2Icon,
  ListIcon,
  MessageSquareIcon,
  ChevronDownIcon,
  SearchIcon,
} from "../components/icons/FeatherIcons";
import { type User, type ActivityRequest, type AuditLog } from "../types";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import ActivityListItem from "../components/ActivityListItem";
import SocialModal from "../components/SocialModal";
import UserSearchModal from "../components/UserSearchModal";
import AuditLogItem from "../components/AuditLogItem";
import PhotoSelectionModal from "../components/PhotoSelectionModal";
import { db, analytics, crashlytics } from "../services/firebase";
import { getUserActivityStats } from "../services/functionsApi";
import { useProfileActivities, type TabId } from "../hooks/useProfileActivities";
import ReportUserModal from "../components/ReportUserModal";
import { createPortal } from "react-dom";
import { CommonFunctions } from "../utils/commonUtils";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Device } from "@capacitor/device";
import { App } from "@capacitor/app";
import { useConfirmUnfollow } from "../hooks/useConfirmUnfollow";
import { useCachedImage } from "../hooks/useCachedImage";

const fallbackImage = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23F3F4F6'/%3E%3Ctext x='50' y='50' fill='%239CA3AF' font-family='sans-serif' font-size='12' text-anchor='middle' dy='.3em'%3EError%3C/text%3E%3C/svg%3E`;

// Default banner image URL
const DEFAULT_BANNER_URL =
  "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?q=80&w=1470&auto=format&fit=crop";

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

interface PortalProps {
  children: React.ReactNode;
}

const Portal: React.FC<PortalProps> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(children, document.body) : null;
};

const RotatingPersonaPassion: React.FC<{
  passions: string[];
  personaTitle: string;
  onClick?: () => void;
}> = ({ passions, personaTitle, onClick }) => {
  const [currentPassionIdx, setCurrentPassionIdx] = useState(0);
  const [showPersonaTitle, setShowPersonaTitle] = useState(false);
  const [highlightPersona, setHighlightPersona] = useState(false);

  useEffect(() => {
    if (!passions || passions.length === 0) return;
    let idx = 0;
    let passionsInterval: NodeJS.Timeout | null = null;
    let personaTimeout: NodeJS.Timeout | null = null;
    let highlightTimeout: NodeJS.Timeout | null = null;

    const startPassionRotation = () => {
      setShowPersonaTitle(false);
      setHighlightPersona(false);
      setCurrentPassionIdx(0);
      idx = 0;
      passionsInterval = setInterval(() => {
        idx++;
        if (idx < passions.length) {
          setCurrentPassionIdx(idx);
        } else {
          if (passionsInterval) clearInterval(passionsInterval);
          personaTimeout = setTimeout(() => {
            setShowPersonaTitle(true);
            highlightTimeout = setTimeout(() => {
              setHighlightPersona(true);
              setTimeout(() => {
                startPassionRotation();
              }, 5000);
            }, 200);
          }, 800);
        }
      }, 1800);
    };

    startPassionRotation();

    return () => {
      if (passionsInterval) clearInterval(passionsInterval);
      if (personaTimeout) clearTimeout(personaTimeout);
      if (highlightTimeout) clearTimeout(highlightTimeout);
    };
  }, [passions]);

  return showPersonaTitle ? (
    <button
      type="button"
      className={`text-xs font-bold italic text-gray-800 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg shadow-md max-w-[140px] md:max-w-[160px] truncate text-center leading-tight whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-500 ${highlightPersona ? "ring-4 ring-primary/40 scale-105 shadow-xl" : ""}`}
      title="Click to see passions"
      onClick={onClick}
    >
      {personaTitle}
    </button>
  ) : (
    <button
      type="button"
      className="text-xs font-bold italic text-gray-800 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg shadow-md max-w-[140px] md:max-w-[160px] truncate text-center leading-tight whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-500"
      title="Click to see passions"
      onClick={onClick}
    >
      <motion.span
        key={passions[currentPassionIdx]}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.5 }}
        className="block"
      >
        {passions[currentPassionIdx]}
      </motion.span>
    </button>
  );
};

// RotatingProfileInfo component for gender * age * location, then bio, with pause
const RotatingProfileInfoComponent: React.FC<{
  gender?: string;
  age?: string | null;
  location?: string;
  bio?: string;
}> = ({ gender, age, location, bio }) => {
  // State machine: 'showingInfo', 'showingBio'
  const [phase, setPhase] = useState<"showingInfo" | "showingBio">(
    "showingInfo",
  );
  const [infoWidth, setInfoWidth] = useState<number | undefined>(undefined);
  const [marqueeDuration, setMarqueeDuration] = useState(30); // seconds
  const infoRef = useRef<HTMLDivElement | null>(null);
  const marqueeInnerRef = useRef<HTMLDivElement | null>(null);

  // Effect to sync width with username
  useEffect(() => {
    let usernameEl = document.getElementById(
      "profile-username",
    ) as HTMLHeadingElement | null;
    if (usernameEl) {
      const width = usernameEl.offsetWidth;
      if (width && width > 0) setInfoWidth(width);
    }
  }, []);

  // Dynamically calculate marquee duration based on text width
  useEffect(() => {
    if (!marqueeInnerRef.current || !infoRef.current) return;
    const textWidth = marqueeInnerRef.current.scrollWidth;
    const containerWidth = infoRef.current.offsetWidth;
    // Minimum duration 6s, max 20s, base speed: 30px/sec (slower)
    let duration = Math.max(6, Math.min(20, (textWidth + containerWidth) / 30));
    setMarqueeDuration(duration);
  }, [phase, infoWidth, gender, age, location, bio]);

  // Map gender to abbreviation
  let genderAbbr: string | undefined = undefined;
  if (gender) {
    const g = gender.trim().toLowerCase();
    if (g === "male" || g === "m") genderAbbr = "M";
    else if (g === "female" || g === "f") genderAbbr = "F";
    else if (g === "other") genderAbbr = "Other";
    // Hide if undisclosed or empty
  }

  // Only show gender if not undisclosed/empty
  const infoItems = [genderAbbr, age, location].filter(Boolean);
  if (infoItems.length === 0 && !bio) return null;

  // Helper to render marquee for a given content
  // Custom keyframe for bio: start from right, scroll left
  // Marquee: always use the same keyframe for both info and bio
  // Marquee: info and bio scroll left, but bio starts fully off-screen right
  // Marquee: both info and bio scroll left, start fully off-screen right
  // Optionally support animation delay for overlapping effect
  const Marquee: React.FC<{
    children: React.ReactNode;
    delay?: number;
    style?: React.CSSProperties;
  }> = ({ children, delay = 0, style }) => (
    <div
      className="overflow-hidden"
      style={{ maxWidth: infoWidth ? infoWidth : "100%" }}
      ref={infoRef}
    >
      <div
        ref={marqueeInnerRef}
        className="marquee-profile-info"
        style={{
          display: "inline-block",
          whiteSpace: "nowrap",
          minWidth: "100%",
          animation: `marquee-profile-info-once ${marqueeDuration}s linear infinite`,
          animationDelay: delay ? `${delay}s` : undefined,
          paddingLeft: infoWidth ? infoWidth : undefined,
          ...style,
        }}
      >
        {children}
        {/* Duplicate for seamless loop */}
        <span
          style={{
            display: "inline-block",
            width: infoWidth ? infoWidth / 2 : 60,
          }}
        />
        {children}
      </div>
    </div>
  );

  // Single marquee: info and bio (or bio and info) in one line, separated by space, for seamless overlap
  const SPACER = (
    <span
      style={{ display: "inline-block", width: infoWidth ? infoWidth / 2 : 60 }}
    />
  );
  return (
    <div
      className="relative mt-0.5 text-sm text-brand-purple flex items-center min-h-[1.1em]"
      style={{
        maxWidth: infoWidth ? infoWidth : "100%",
        width: infoWidth ? infoWidth : "fit-content",
        lineHeight: 1.1,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Marquee>
        {phase === "showingInfo" ? (
          <>
            {infoItems.map((item, idx, arr) => (
              <React.Fragment key={idx}>
                <span className="mx-0.5 font-thin text-brand-purple-dark">
                  {item}
                </span>
                {idx < arr.length - 1 && (
                  <span className="mx-0.5 text-brand-purple-dark">&bull;</span>
                )}
              </React.Fragment>
            ))}
            {bio && (
              <>
                {SPACER}
                <span className="mx-0.5 font-thin text-brand-purple-dark">
                  {bio}
                </span>
              </>
            )}
          </>
        ) : (
          <>
            {bio && (
              <>
                <span className="mx-0.5 font-thin text-brand-purple-dark">
                  {bio}
                </span>
                {SPACER}
              </>
            )}
            {infoItems.map((item, idx, arr) => (
              <React.Fragment key={idx}>
                <span className="mx-0.5 font-thin text-brand-purple-dark">
                  {item}
                </span>
                {idx < arr.length - 1 && (
                  <span className="mx-0.5 text-brand-purple-dark">&bull;</span>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </Marquee>
    </div>
  );
};

const RotatingProfileInfo = React.memo(
  RotatingProfileInfoComponent,
  (prev, next) => {
    // Only rerender if info props change
    return (
      prev.gender === next.gender &&
      prev.age === next.age &&
      prev.location === next.location &&
      prev.bio === next.bio
    );
  },
);

const ProfilePage: React.FC = () => {
  // Modal for profile details
  const [isProfileDetailsModalOpen, setIsProfileDetailsModalOpen] =
    useState(false);
  usePageTracking("Profile", "ProfilePage");
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [displayedUser, setDisplayedUser] = useState<User | null>(null);
  const { userId } = useParams<{ userId: string }>();
  const {
    currentUser,
    getUserById,
    requestFollow,
    cancelFollowRequest,
    updateUser,
  } = useAuth();
  const { passedActivityIds } = useActivityFeedData();
  const { getAuditLogsForUser } = useActivityAuditData();
  const {
    loadAuditLogs,
    createOrGetDirectChat,
    joinActivity,
    undeleteActivity,
  } = useActivityActions();

  const {
    created,
    joined,
    expired,
    declined,
    deleted,
    loading,
    loaded,
    loadCreatedTab,
    loadJoinedTab,
    loadExpiredTab,
    loadDeclinedTab,
    loadDeletedTab,
  } = useProfileActivities({ displayedUser, currentUser, getUserById });

  const [activeTab, setActiveTab] = useState<TabId>("created");
  const { confirmUnfollow, ConfirmUnfollowModal } = useConfirmUnfollow();

  // Memoize passionsArray to avoid unnecessary rerenders
  const passionsArray = useMemo(
    () =>
      displayedUser &&
        Array.isArray(displayedUser.passions) &&
        displayedUser.passions.length > 0
        ? displayedUser.passions
        : null,
    [
      displayedUser?.id,
      Array.isArray(displayedUser?.passions)
        ? displayedUser.passions.join(",")
        : "",
    ],
  );

  // Memoize personaTitle
  const personaTitle = useMemo(
    () => displayedUser?.personaTitle || "",
    [displayedUser?.id, displayedUser?.personaTitle],
  );
  const [showPassionsModal, setShowPassionsModal] = useState(false);

  // Use cached image for profile
  const profileImage = useCachedImage(
    displayedUser?.profileImageUrl || "",
    displayedUser?.username || "",
    displayedUser?.id || "",
  );

  // Use cached image for banner
  const bannerImage = useCachedImage(
    DEFAULT_BANNER_URL,
    "profile-banner",
    "default-banner",
  );

  // Calculate displayed user's age
  let displayedUserAge = CommonFunctions.calculateAge(
    displayedUser?.dateOfBirth || "",
  );

  useEffect(() => {
    if (!displayedUser) return;
    if (activeTab === "created") {
      loadCreatedTab();
    } else if (activeTab === "joined") {
      loadJoinedTab();
    } else if (activeTab === "expired") {
      loadExpiredTab();
    } else if (activeTab === "declined") {
      loadDeclinedTab();
    } else if (activeTab === "deleted") {
      loadDeletedTab();
    }
  }, [activeTab, displayedUser, loadCreatedTab, loadJoinedTab, loadExpiredTab, loadDeclinedTab, loadDeletedTab]);

  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Removed duplicate declaration of displayedUser and setDisplayedUser
  const [isLoading, setIsLoading] = useState(true);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const [joiningActivityId, setJoiningActivityId] = useState<string | null>(
    null,
  );
  const [undeletingActivityId, setUndeletingActivityId] = useState<
    string | null
  >(null);
  const [followersModalOpen, setFollowersModalOpen] = useState(false);
  const [followingModalOpen, setFollowingModalOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const isCurrentUserProfile =
    !userId || (currentUser && currentUser.id === userId);

  // Handler for reporting a user (opens modal)
  const handleReportUser = () => {
    if (!currentUser || !displayedUser) return;
    setIsReportModalOpen(true);
  };

  // Handles actual report submission to Firestore for both users
  const handleReportSubmit = async (reason: string) => {
    if (!currentUser || !displayedUser) return;
    // Calculate activity counts for displayedUser (profile being viewed)
    const displayedUserActivitiesCreated = createdActivities.length;
    const displayedUserActivitiesJoined = joinedActivities.length;
    const displayedUserExpiredActivities = expiredActivities.length;
    const displayedUserDeclinedActivities = declinedActivities.length;
    const displayedUserDeletedActivities = deletedActivities.length;

    // Fetch all activities for currentUser and use the same categorization logic as displayedUser
    let globalCurrentUserStats = {
      activitiesCreated: 0,
      activitiesJoined: 0,
      activitiesExpired: 0,
      activitiesDeclined: 0,
      activitiesDeleted: 0,
      allActivitiesCount: 0,
    };
    try {
      const statsResponse = await getUserActivityStats({
        userId: currentUser.id,
      });

      if (statsResponse?.success && statsResponse.stats) {
        globalCurrentUserStats = statsResponse.stats;
      }
    } catch (e) {
      console.warn("Could not fetch global activity stats for currentUser:", e);
    }

    // Get platform/device/app info using Capacitor if available
    let platform = undefined;
    let deviceModel = undefined;
    let deviceOsVersion = undefined;
    let deviceManufacturer = undefined;
    let deviceName = undefined;
    let appVersion = undefined;
    try {
      const cap = (window as any).Capacitor || undefined;
      if (cap && cap.isNativePlatform && cap.isNativePlatform()) {
        const [deviceInfo, appInfo] = await Promise.all([
          Device.getInfo(),
          App.getInfo(),
        ]);
        platform =
          deviceInfo.platform || deviceInfo.operatingSystem || undefined;
        deviceModel = deviceInfo.model || undefined;
        deviceOsVersion = deviceInfo.osVersion || undefined;
        deviceManufacturer = deviceInfo.manufacturer || undefined;
        deviceName = deviceInfo.name || undefined;
        appVersion = appInfo.version || undefined;
      } else if (navigator.platform) {
        platform = navigator.platform;
        appVersion = navigator.appVersion;
      }
    } catch (e) {
      // fallback: do nothing, platform/appVersion stay undefined
      console.warn("Could not get device/app info for report:", e);
    }

    const rawReportData = {
      deviceInfo: {
        platform,
        deviceModel,
        deviceOsVersion,
        deviceManufacturer,
        deviceName,
        userAgent: navigator.userAgent,
        appVersion,
      },
      userReport: {
        reportedUserId: displayedUser.id,
        reportedUsername: displayedUser.username,
        reportedEmail: displayedUser.email || "",
        reportedStats: {
          allActivitiesCount:
            createdActivities.length +
            joinedActivities.length +
            expiredActivities.length +
            declinedActivities.length +
            deletedActivities.length,
          activitiesCreated: displayedUserActivitiesCreated,
          activitiesJoined: displayedUserActivitiesJoined,
          activitiesExpired: displayedUserExpiredActivities,
          activitiesDeclined: displayedUserDeclinedActivities,
          activitiesDeleted: displayedUserDeletedActivities,
          followers: Array.isArray(displayedUser.followers)
            ? displayedUser.followers.length
            : 0,
          following: Array.isArray(displayedUser.following)
            ? displayedUser.following.length
            : 0,
        },
        reporterUserId: currentUser.id,
        reporterUsername: currentUser.username,
        reporterEmail: currentUser.email || "",
        reporterStats: {
          allActivitiesCount: globalCurrentUserStats.allActivitiesCount,
          activitiesCreated: globalCurrentUserStats.activitiesCreated,
          activitiesJoined: globalCurrentUserStats.activitiesJoined,
          activitiesExpired: globalCurrentUserStats.activitiesExpired,
          activitiesDeclined: globalCurrentUserStats.activitiesDeclined,
          activitiesDeleted: globalCurrentUserStats.activitiesDeleted,
          followers: Array.isArray(currentUser.followers)
            ? currentUser.followers.length
            : 0,
          following: Array.isArray(currentUser.following)
            ? currentUser.following.length
            : 0,
        },
        reason,
        context: {
          page: "ProfilePage",
          reportedUserProfileId: displayedUser.id,
          reporterProfileId: currentUser.id,
        },
      },
      status: "pending",
      statusUpdatedBy: "system",
      statusUpdatedAt: serverTimestamp(),
      auditNotes: "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const reportData = CommonFunctions.removeUndefined(rawReportData);
    if (!db) return;
    await addDoc(
      collection(db, "reports", "userReports", "entries"),
      reportData,
    );
  };

  const allowedTabs: TabId[] = ["created", "joined", "expired", "declined", "deleted", "audit"];
  const LAST_PROFILE_TAB_KEY = "profile:lastTab";

  // Pick tab from URL/state/localStorage when location changes (avoid using activeTab to prevent flicker)
  useEffect(() => {
    const state = location.state as { initialTab?: string } | null;
    const urlParams = new URLSearchParams(location.search);
    const tabParam = state?.initialTab || urlParams.get("tab");
    const storedTab = localStorage.getItem(LAST_PROFILE_TAB_KEY) as TabId | null;

    const candidate = [tabParam, storedTab, "created"].find(
      (t) => t && allowedTabs.includes(t as TabId),
    ) as TabId | undefined;

    if (candidate) {
      setActiveTab(candidate);
    }
  }, [location.state, location.search]);

  // Load audit logs when History tab is opened
  useEffect(() => {
    if (activeTab === "audit") {
      loadAuditLogs();
    }
  }, [activeTab, loadAuditLogs]);

  // Persist selected tab for return navigation
  useEffect(() => {
    if (allowedTabs.includes(activeTab)) {
      localStorage.setItem(LAST_PROFILE_TAB_KEY, activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    let isMounted = true;
    const fetchUser = async () => {
      if (!userId && !currentUser) return;
      setIsLoading(true);
      const id = userId ? userId : currentUser?.id;
      if (id) {
        const user = await getUserById(id);
        if (isMounted) setDisplayedUser(user || null);
      } else if (!userId && isMounted) {
        setDisplayedUser(currentUser);
      }
      if (isMounted) setIsLoading(false);
    };
    fetchUser();
    return () => {
      isMounted = false;
    };
  }, [userId, currentUser, getUserById]); // Only run when these change

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target as Node)
      ) {
        setIsMoreMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper to get display values
  const displayName = displayedUser?.username || "";
  const displayGender =
    displayedUser?.gender && displayedUser.gender !== "undisclosed"
      ? displayedUser.gender
      : "—";
  const displayAge =
    displayedUserAge > 0
      ? displayedUserAge
      : displayedUser?.dateOfBirth
        ? CommonFunctions.calculateAge(displayedUser.dateOfBirth)
        : "—";
  const displayBio = displayedUser?.bio || "—";

  const {
    createdActivities,
    joinedActivities,
    expiredActivities,
    declinedActivities,
    deletedActivities,
    userAuditLogs,
  } = useMemo(() => {
    const sortByDateTimeAsc = (arr: ActivityRequest[]) =>
      arr.slice().sort((a, b) => {
        const aDate = toJsDate(a.dateTime).getTime();
        const bDate = toJsDate(b.dateTime).getTime();
        return aDate - bDate;
      });
    const sortByDateTimeDesc = (arr: ActivityRequest[]) =>
      arr.slice().sort((a, b) => {
        const aDate = toJsDate(a.dateTime).getTime();
        const bDate = toJsDate(b.dateTime).getTime();
        return bDate - aDate;
      });

    return {
      createdActivities: sortByDateTimeAsc(created),
      joinedActivities: sortByDateTimeAsc(joined),
      expiredActivities: sortByDateTimeDesc(expired),
      declinedActivities: sortByDateTimeAsc(declined),
      deletedActivities: sortByDateTimeDesc(deleted),
      userAuditLogs: displayedUser ? getAuditLogsForUser(displayedUser.id) : [],
    };
  }, [created, joined, expired, declined, deleted, displayedUser, getAuditLogsForUser]);

  const isFollowing = currentUser?.following?.includes(
    displayedUser?.id ?? "-1",
  );
  const hasRequested = currentUser?.requestedFollowing?.includes(
    displayedUser?.id ?? "-1",
  );
  const isFriend =
    isFollowing && displayedUser?.following?.includes(currentUser?.id ?? "-1");

  const canViewContent = isCurrentUserProfile || isFollowing || isFriend;

  const handleFollowAction = () => {
    if (!displayedUser) return;
    if (isFollowing) {
      confirmUnfollow(displayedUser.id);
    } else if (hasRequested) {
      cancelFollowRequest(displayedUser.id);
    } else {
      requestFollow(displayedUser.id);
    }
  };

  const handleMessage = async () => {
    if (!displayedUser) return;
    try {
      const chatId = await createOrGetDirectChat(displayedUser.id);
      navigate(`/chat/${chatId}`);
    } catch (error) {
      crashlytics.logError(
        `Error creating or getting direct chat: ${JSON.stringify(error)}`,
        "ProfilePage.tsx",
        error,
      );
    }
  };

  const handleJoinActivity = (activityId: string) => {
    setJoiningActivityId(activityId);

    const prefetchedActivity = selectProfileJoinPrefetchedActivity(activityId, [
      createdActivities,
      joinedActivities,
      expiredActivities,
      declinedActivities,
      deletedActivities,
    ]);

    const matchNavigation = createMatchNavigationTarget(
      activityId,
      prefetchedActivity,
    );
    navigate(matchNavigation.path, {
      state: matchNavigation.state,
    });

    void joinActivity(activityId)
      .then((result) => {
        const outcome = resolveProfileJoinResult(activityId, result);
        addToast(outcome.toast.message, outcome.toast.type);

        if (outcome.activeTab) {
          setActiveTab("joined");
          return;
        }

        if (outcome.navigation) {
          navigate(outcome.navigation.path, {
            replace: outcome.navigation.replace,
          });
        }
      })
      .catch((error) => {
        crashlytics.logError(
          `Error joining activity: ${JSON.stringify(error)}`,
          "ProfilePage.tsx",
          error,
        );
        const outcome = resolveProfileJoinError(activityId);
        addToast(outcome.toast.message, outcome.toast.type);
        navigate(outcome.navigation.path, {
          replace: outcome.navigation.replace,
        });
      })
      .finally(() => {
        setJoiningActivityId(null);
      });
  };

  const handleUndeleteActivity = async (activityId: string) => {
    try {
      setUndeletingActivityId(activityId);
      await undeleteActivity(activityId);
      setActiveTab("created");
      setUndeletingActivityId(null);
    } catch (error) {
      crashlytics.logError(
        `Error restoring activity: ${JSON.stringify(error)}`,
        "ProfilePage.tsx",
        error,
      );
      addToast("An error occurred while restoring the activity.", "error");
      setUndeletingActivityId(null);
    }
  };

  const handlePhotoSelect = async (url: string) => {
    if (!currentUser) return;

    try {
      await updateUser({ profileImageUrl: url });
      addToast("Profile picture updated successfully!", "success");
      setIsPhotoModalOpen(false);
    } catch (error) {
      crashlytics.logError(
        `Error updating profile picture: ${JSON.stringify(error)}`,
        "ProfilePage.tsx",
        error,
      );
      addToast("Failed to update profile picture. Please try again.", "error");
    }
  };

  const getFollowButton = () => {
    if (isCurrentUserProfile) {
      return (
        <button
          onClick={() => navigate("/profile/edit")}
          className="p-2 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-primary transition-colors"
        >
          <Edit2Icon className="w-4 h-4" />
        </button>
      );
    }

    if (isFriend) {
      return (
        <button
          onClick={handleMessage}
          className="bg-primary text-white font-bold text-sm py-2 px-6 rounded-full transition-all transform hover:scale-105 shadow-sm flex items-center"
        >
          <MessageSquareIcon className="w-4 h-4 mr-2" /> Message
        </button>
      );
    }

    let text = "Follow";
    let className = "bg-primary text-white";

    if (isFollowing) {
      text = "Following";
      className = "border border-gray-300 bg-white text-gray-800";
    } else if (hasRequested) {
      text = "Requested";
      className = "border border-gray-200 bg-gray-100 text-gray-500";
    }

    return (
      <button
        onClick={handleFollowAction}
        className={`${className} font-bold text-sm py-2 px-6 rounded-full transition-all transform hover:scale-105 shadow-sm`}
      >
        {text}
      </button>
    );
  };

  const mainTabs = useMemo(
    () => [
      { id: "created", label: "Created" },
      { id: "joined", label: "Joined" },
      ...(isCurrentUserProfile ? [{ id: "declined", label: "Declined" }] : []),
    ],
    [isCurrentUserProfile],
  );

  const moreTabs = useMemo(() => {
    if (!isCurrentUserProfile) return [];
    return [
      { id: "expired", label: "Expired" },
      { id: "deleted", label: "Deleted" },
      { id: "audit", label: "History" },
    ];
  }, [isCurrentUserProfile]);

  const isMoreTabActive = moreTabs.some((t) => t.id === activeTab);

  const renderTabContent = () => {
    let activitiesToShow: ActivityRequest[] = [];
    let emptyMessage = "No activities yet.";
    let emptyIcon = <></>;
    const tabLoading =
      activeTab === "created"
        ? loading.created
        : activeTab === "deleted"
          ? loading.deleted
        : activeTab === "joined"
          ? loading.joined
          : activeTab === "expired"
            ? loading.expired
            : activeTab === "declined"
              ? loading.declined
              : false;

    if (activeTab === "created") {
      activitiesToShow = createdActivities;
      emptyMessage = isCurrentUserProfile
        ? `${currentUser?.username} hasn't created any activities yet.`
        : `${displayedUser?.username} hasn't created any activities.`;
    } else if (activeTab === "joined") {
      activitiesToShow = joinedActivities;
      emptyMessage = isCurrentUserProfile
        ? `${currentUser?.username} hasn't joined any activities.`
        : `${displayedUser?.username} hasn't joined any activities.`;
    } else if (activeTab === "expired") {
      activitiesToShow = expiredActivities;
      emptyMessage = isCurrentUserProfile
        ? `${currentUser?.username} has no past activities.`
        : `${displayedUser?.username} has no past activities.`;
    } else if (activeTab === "declined") {
      activitiesToShow = declinedActivities;
      emptyMessage = isCurrentUserProfile
        ? `${currentUser?.username} hasn't passed on any activities.`
        : `${displayedUser?.username} hasn't passed on any activities.`;
    } else if (activeTab === "deleted") {
      activitiesToShow = deletedActivities;
      emptyMessage = "No recently deleted activities.";
      emptyIcon = <Trash2Icon className="w-12 h-12 text-gray-400/50 mb-4" />;
    } else if (activeTab === "audit") {
      if (userAuditLogs.length === 0) {
        return (
          <div className="text-center pt-16 px-4 flex flex-col items-center">
            <ListIcon className="w-12 h-12 text-gray-400/50 mb-4" />
            <p className="text-lg font-semibold text-gray-500">
              No History Yet
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Your actions will be recorded here.
            </p>
          </div>
        );
      }
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="pt-2"
        >
          <div className="flex flex-col space-y-1">
            {userAuditLogs.map((log) => (
              <AuditLogItem key={log.id} log={log} />
            ))}
          </div>
        </motion.div>
      );
    }

    // Show loading spinner while activities are being fetched
    if (tabLoading) {
      return (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
        </div>
      );
    }

    if (activitiesToShow.length === 0) {
      return (
        <div className="text-center pt-16 px-4 flex flex-col items-center">
          {emptyIcon}
          <p className="text-lg font-semibold text-gray-500">{emptyMessage}</p>
          {isCurrentUserProfile && activeTab === "created" && (
            <button
              onClick={() => navigate("/create")}
              className="mt-4 bg-primary text-white font-bold py-2 px-6 rounded-full"
            >
              Create One
            </button>
          )}
        </div>
      );
    }

    // log analytics event for viewing activities
    // analytics.logEvent(`profile_page_view_${activeTab}`, {
    //     userId: displayedUser?.id || 'unknown',
    //     tab: activeTab,
    //     activityCount: activitiesToShow.length,
    // });
    // crashlytics.log(`Viewing ${activitiesToShow.length} activities in tab: ${activeTab} on ProfilePage for userId: ${displayedUser?.id || 'unknown'}`);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pt-2"
      >
        <div className="flex flex-col space-y-2">
          {activitiesToShow.map((activity) => {
            // Check if this is a pending invite in the joined tab
            const isPendingInvite =
              activeTab === "joined" &&
              activity.pendingInvites &&
              activity.pendingInvites.includes(currentUser?.id || "") &&
              !activity.participantIds?.includes(currentUser?.id || "");
            const shouldShowOrganizerIndicator = true;
            const originLabels: string[] = [];
            let isExpiredDeleted = false;
            if (activeTab === "expired") {
              const isCreatedByProfileUser =
                !!displayedUser?.id && activity.creatorId === displayedUser.id;
              if (isCreatedByProfileUser) {
                originLabels.push("Created");
              }
              if (activity.isDeleted) {
                originLabels.push("Deleted");
              }
            } else if (activeTab === "deleted") {
              const endTime =
                toJsDate(activity.dateTime).getTime() +
                (activity.duration || 0) * 60 * 1000;
              isExpiredDeleted = !Number.isNaN(endTime) && endTime < Date.now();
              if (isExpiredDeleted) {
                originLabels.push("Expired");
              }
            }

            const canRestore =
              activeTab === "deleted" &&
              isCurrentUserProfile &&
              activity.creatorId === currentUser?.id &&
              !isExpiredDeleted;

            return (
              <ActivityListItem
                key={activity.id}
                activity={activity}
                onClick={() => navigate(`/activity/${activity.id}`)}
                showJoinButton={
                  !!(activeTab === "declined" && isCurrentUserProfile)
                }
                showUndeleteButton={
                  !!canRestore
                }
                onJoin={handleJoinActivity}
                onUndelete={handleUndeleteActivity}
                isJoining={joiningActivityId === activity.id}
                isUndeleting={undeletingActivityId === activity.id}
                currentUserId={currentUser?.id}
                passedActivityIds={passedActivityIds}
                showPendingInviteBadge={isPendingInvite}
                showOrganizerIndicator={shouldShowOrganizerIndicator}
                hiddenStatusLabels={
                  activeTab === "joined"
                    ? ["Joined"]
                    : activeTab === "declined"
                      ? ["Declined"]
                      : []
                }
                originLabels={originLabels.length ? originLabels : undefined}
              />
            );
          })}
        </div>
      </motion.div>
    );
  };


  if (isLoading || !displayedUser || !currentUser) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      {ConfirmUnfollowModal}
      {/* Followers Modal */}
      {followersModalOpen && (
        <SocialModal
          isOpen={followersModalOpen}
          onClose={() => setFollowersModalOpen(false)}
          type="followers"
          followers={displayedUser.followers || []}
          following={displayedUser.following || []}
          pendingFollowers={displayedUser.pendingFollowers || []}
          requestedFollowing={displayedUser.requestedFollowing || []}
          isCurrentUserProfile={!!isCurrentUserProfile}
        />
      )}
      {/* Following Modal */}
      {followingModalOpen && (
        <SocialModal
          isOpen={followingModalOpen}
          onClose={() => setFollowingModalOpen(false)}
          type="following"
          followers={displayedUser.followers || []}
          following={displayedUser.following || []}
          pendingFollowers={displayedUser.pendingFollowers || []}
          requestedFollowing={displayedUser.requestedFollowing || []}
          isCurrentUserProfile={!!isCurrentUserProfile}
        />
      )}
      {/* Report User Modal */}
      <Portal>
        <ReportUserModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          onSubmit={handleReportSubmit}
          reportedUsername={displayedUser?.username || ""}
          reporterEmail={currentUser?.email || ""}
          reporterUsername={currentUser?.username || ""}
        />
      </Portal>
      {/* User Search Modal */}
      <Portal>
        <UserSearchModal
          isOpen={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
        />
      </Portal>
      {/* Photo Selection Modal */}
      {isCurrentUserProfile && isPhotoModalOpen && (
        <PhotoSelectionModal
          isOpen={isPhotoModalOpen}
          onClose={() => setIsPhotoModalOpen(false)}
          onSelect={handlePhotoSelect}
          allowMultiple={false}
          currentPhoto={displayedUser?.profileImageUrl}
        />
      )}

      <div className="font-sans h-full flex flex-col overflow-x-hidden">
        {/* Header buttons are positioned absolutely within this container */}
        <div
          className="absolute inset-x-4 flex justify-between items-center z-30"
          style={{
            top: `calc(max(env(safe-area-inset-top, 0px), 0px) + 1rem)`,
          }}
        >
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-black/30 text-white hover:bg-black/40 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          {isCurrentUserProfile ? (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSearchModalOpen(true)}
                className="p-2 rounded-full bg-black/30 text-white hover:bg-black/40 transition-colors"
              >
                <SearchIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate("/settings")}
                className="p-2 rounded-full bg-black/30 text-white hover:bg-black/40 transition-colors"
              >
                <MoreVerticalIcon className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleReportUser}
              className="px-2 py-1 rounded-full bg-red-100 text-xs text-red-600 hover:bg-red-200 transition-colors font-semibold"
              style={{
                minWidth: 0,
                height: "28px",
                fontSize: "0.85rem",
                lineHeight: 1,
              }}
              title="Report this user"
            >
              Report
            </button>
          )}
        </div>
        {/* Main content area */}
        <div className="relative flex flex-col h-full">
          {/* Banner-style Cover Photo */}
          <div className="h-48 md:h-56 bg-gray-200 flex-shrink-0 overflow-hidden">
            <img
              src={bannerImage}
              onError={handleImageError}
              alt="Cover photo of New York City"
              className="w-full h-full object-cover max-w-full"
            />
          </div>

          {/* Content Card that overlaps the cover */}
          <div className="relative -mt-16 md:-mt-20 bg-white rounded-t-3xl pt-4 flex-1 flex flex-col min-h-0">
            <div className="max-w-3xl mx-auto px-4 flex-1 flex flex-col min-h-0 w-full">
              {/* Overlapping Profile Picture */}
              <div className="absolute -top-16 left-4 md:-top-20 md:left-6 flex-shrink-0">
                <div className="relative">
                  <div
                    className={`w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-white bg-gray-200 flex-shrink-0 shadow-lg overflow-hidden ${isCurrentUserProfile ? "cursor-pointer group" : ""}`}
                  >
                    {(() => {
                      return (
                        <img
                          key={displayedUser?.profileImageUrl}
                          src={profileImage}
                          referrerPolicy="no-referrer"
                          onError={handleImageError}
                          alt={displayedUser?.username}
                          className={`w-full h-full object-cover ${isCurrentUserProfile ? "group-hover:opacity-75 transition-opacity" : ""}`}
                          onClick={
                            isCurrentUserProfile
                              ? () => setIsPhotoModalOpen(true)
                              : undefined
                          }
                        />
                      );
                    })()}
                    {isCurrentUserProfile && (
                      <div
                        className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-full"
                        onClick={() => setIsPhotoModalOpen(true)}
                      >
                        <Edit2Icon className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Persona Title Text - Starting from right lower end of circle */}
                  <div className="absolute bottom-0 right-0 md:bottom-2 md:right-2 transform translate-x-1/2 translate-y-1/4">
                    {passionsArray && personaTitle && (
                      <RotatingPersonaPassion
                        passions={passionsArray}
                        personaTitle={personaTitle}
                        onClick={() => setShowPassionsModal(true)}
                      />
                    )}
                  </div>
                  {/* Passions Modal/Tooltip - Purple Theme */}
                  {showPassionsModal &&
                    passionsArray &&
                    passionsArray.length > 0 && (
                      <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
                        onClick={() => setShowPassionsModal(false)}
                      >
                        <div
                          className="bg-white rounded-2xl shadow-xl p-6 max-w-xs w-full mx-4 relative border border-gray-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="absolute top-2 right-2 text-gray-400 hover:text-primary text-xl font-bold focus:outline-none"
                            onClick={() => setShowPassionsModal(false)}
                            aria-label="Close passions modal"
                          >
                            &times;
                          </button>
                          <h2 className="text-xl font-extrabold mb-4 text-center text-primary tracking-tight italic">
                            {personaTitle}
                          </h2>
                          <div className="flex flex-wrap justify-center gap-2">
                            {passionsArray.map(
                              (passion: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium border border-primary/20"
                                >
                                  {passion}
                                </span>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* Follow Button */}
              <div className="flex justify-end items-start mb-10 flex-shrink-0">
                <div>{getFollowButton()}</div>
              </div>

              {/* Username and Social Stats Section */}
              <div className="mb-2 flex-shrink-0">
                {/* Username and Followers/Following in one line */}
                <div className="flex items-center justify-between gap-4 mb-2">
                  {/* Username and rotating info: clickable for modal */}
                  <div
                    className="flex-1 min-w-0 cursor-pointer group"
                    onClick={() => setIsProfileDetailsModalOpen(true)}
                    title="View profile details"
                  >
                    <h1
                      id="profile-username"
                      className="text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-primary transition-colors truncate"
                      style={{ fontSize: "clamp(1.25rem, 5vw, 2rem)" }}
                    >
                      {displayedUser.username}
                    </h1>
                    {(displayedUser.gender ||
                      displayedUserAge > 0 ||
                      displayedUser.location ||
                      displayedUser.bio) && (
                        <RotatingProfileInfo
                          gender={displayedUser.gender}
                          age={
                            displayedUserAge > 0
                              ? `${displayedUserAge} years`
                              : displayedUser.dateOfBirth
                                ? `${CommonFunctions.calculateAge(displayedUser.dateOfBirth)} years`
                                : null
                          }
                          location={displayedUser.location}
                          bio={displayedUser.bio}
                        />
                      )}
                  </div>

                  {/* Followers/Following - compact and responsive */}
                  <div className="flex items-center space-x-3 md:space-x-4 flex-shrink-0 min-w-0">
                    <button
                      onClick={() => setFollowersModalOpen(true)}
                      className="text-center relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg p-1.5 hover:bg-gray-50 transition-colors min-w-0"
                    >
                      <div className="flex items-center space-x-1">
                        <p className="text-lg md:text-xl font-bold text-gray-900 flex-shrink-0">
                          {displayedUser.followers?.length ?? 0}
                        </p>
                        <p className="text-sm md:text-base text-gray-500 truncate">
                          Followers
                        </p>
                        {isCurrentUserProfile &&
                          (displayedUser.pendingFollowers?.length ?? 0) > 0 && (
                            <div className="ml-0.5 min-w-[16px] h-[16px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-0.5 shadow-md animate-pulse flex-shrink-0">
                              {(displayedUser.pendingFollowers?.length ?? 0) >
                                99
                                ? "99+"
                                : (displayedUser.pendingFollowers?.length ?? 0)}
                            </div>
                          )}
                      </div>
                    </button>

                    <button
                      onClick={() => setFollowingModalOpen(true)}
                      className="text-center relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg p-1.5 hover:bg-gray-50 transition-colors min-w-0"
                    >
                      <div className="flex items-center space-x-1">
                        <p className="text-lg md:text-xl font-bold text-gray-900 flex-shrink-0">
                          {displayedUser.following?.length ?? 0}
                        </p>
                        <p className="text-sm md:text-base text-gray-500 truncate">
                          Following
                        </p>
                        {isCurrentUserProfile &&
                          (displayedUser.requestedFollowing?.length ?? 0) >
                          0 && (
                            <div className="ml-0.5 min-w-[16px] h-[16px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-0.5 shadow-md animate-pulse flex-shrink-0">
                              {(displayedUser.requestedFollowing?.length ?? 0) >
                                99
                                ? "99+"
                                : (displayedUser.requestedFollowing?.length ??
                                  0)}
                            </div>
                          )}
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              {/* Profile Details Modal */}
              <AnimatePresence>
                {isProfileDetailsModalOpen && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                    onClick={() => setIsProfileDetailsModalOpen(false)}
                  >
                    <motion.div
                      initial={{ scale: 0.97, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.97, opacity: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 320,
                        damping: 28,
                      }}
                      className="bg-brand-purple-light border border-primary/20 rounded-3xl shadow-2xl w-full max-w-xs md:max-w-sm p-0 relative flex flex-col items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="absolute top-3 right-3 text-gray-400 hover:text-primary text-2xl font-bold focus:outline-none z-10"
                        onClick={() => setIsProfileDetailsModalOpen(false)}
                        aria-label="Close profile details modal"
                      >
                        &times;
                      </button>
                      <div className="w-full flex flex-col items-center pt-8 pb-2 px-6">
                        <div className="flex items-center justify-center mb-2">
                          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-1 border-2 border-primary/20">
                            <svg
                              width="36"
                              height="36"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              className="text-primary"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                              />
                            </svg>
                          </div>
                        </div>
                        <h2 className="text-xl font-extrabold mb-4 text-center text-primary tracking-tight">
                          Profile Details
                        </h2>
                        <div className="grid grid-cols-1 gap-y-4 w-full">
                          <div className="flex flex-col items-start">
                            <span className="text-xs text-primary font-semibold uppercase mb-0.5 tracking-wide">
                              Name
                            </span>
                            <span className="text-base font-bold text-gray-900 break-all">
                              {displayName}
                            </span>
                          </div>
                          <div className="flex flex-col items-start">
                            <span className="text-xs text-primary font-semibold uppercase mb-0.5 tracking-wide">
                              Gender
                            </span>
                            <span className="text-base text-gray-900">
                              {displayGender}
                            </span>
                          </div>
                          <div className="flex flex-col items-start">
                            <span className="text-xs text-primary font-semibold uppercase mb-0.5 tracking-wide">
                              Age
                            </span>
                            <span className="text-base text-gray-900">
                              {displayAge}
                            </span>
                          </div>
                          <div className="flex flex-col items-start">
                            <span className="text-xs text-primary font-semibold uppercase mb-0.5 tracking-wide">
                              Bio
                            </span>
                            <span className="text-base text-gray-900 whitespace-pre-line break-words">
                              {displayBio}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              <LayoutGroup>
                <div className="mt-1 flex-1 flex flex-col min-h-0">
                  <div className="space-y-3 flex-1 flex flex-col min-h-0">
                    {canViewContent ? (
                      <div className="flex flex-col flex-1 min-h-0">
                        <div
                          className="p-1 bg-gray-100 rounded-xl flex items-center relative shadow-inner flex-shrink-0"
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                        >
                          {mainTabs.map((tab) => (
                            <button
                              key={tab.id}
                              onClick={() => setActiveTab(tab.id as any)}
                              className={`w-full relative z-10 py-2 text-sm font-bold transition-colors rounded-full ${activeTab === tab.id ? "" : "text-gray-500 hover:text-gray-900"}`}
                            >
                              {activeTab === tab.id && (
                                <motion.div
                                  layoutId="pill"
                                  className="absolute inset-0 bg-primary rounded-full shadow-md"
                                  style={{ borderRadius: 9999 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 350,
                                    damping: 30,
                                  }}
                                />
                              )}
                              <span
                                className={`relative z-10 ${activeTab === tab.id ? "text-white" : ""}`}
                              >
                                {tab.label}
                              </span>
                            </button>
                          ))}

                          {moreTabs.length > 0 && (
                            <div className="relative w-full" ref={moreMenuRef}>
                              <button
                                onClick={() =>
                                  setIsMoreMenuOpen(!isMoreMenuOpen)
                                }
                                className={`w-full relative z-10 py-2 text-sm font-bold transition-colors rounded-full flex items-center justify-center gap-1 ${isMoreTabActive ? "" : "text-gray-500 hover:text-gray-900"}`}
                              >
                                {isMoreTabActive && (
                                  <motion.div
                                    layoutId="pill"
                                    className="absolute inset-0 bg-primary rounded-full shadow-md"
                                    style={{ borderRadius: 9999 }}
                                    transition={{
                                      type: "spring",
                                      stiffness: 350,
                                      damping: 30,
                                    }}
                                  />
                                )}
                                <span
                                  className={`relative z-10 ${isMoreTabActive ? "text-white" : ""}`}
                                >
                                  {isMoreTabActive
                                    ? moreTabs.find((t) => t.id === activeTab)
                                      ?.label
                                    : "More"}
                                </span>
                                <ChevronDownIcon
                                  className={`w-4 h-4 transition-transform ${isMoreMenuOpen ? "rotate-180" : ""} ${isMoreTabActive ? "text-white" : ""}`}
                                />
                              </button>
                              <AnimatePresence>
                                {isMoreMenuOpen && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1"
                                  >
                                    {moreTabs.map((tab) => (
                                      <button
                                        key={tab.id}
                                        onClick={() => {
                                          setActiveTab(tab.id as any);
                                          setIsMoreMenuOpen(false);
                                        }}
                                        className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        {tab.label}
                                      </button>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}
                        </div>

                        {/* Scrollable Content Area - Flex grows to fill remaining space */}
                        <div
                          className="overflow-y-auto overscroll-contain flex-1 min-h-0"
                          style={{
                            WebkitOverflowScrolling: "touch",
                          }}
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                        >
                          <AnimatePresence mode="wait">
                            {renderTabContent()}
                          </AnimatePresence>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center pt-16 pb-8 px-4 flex flex-col items-center">
                        <LockIcon className="w-12 h-12 text-gray-400/50 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-500 mb-2">
                          Private Profile
                        </h3>
                        <p className="text-sm text-gray-500 max-w-xs">
                          This user's profile is private. Follow them to see
                          their activities.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </LayoutGroup>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
